import { useState } from 'react';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Spinner,
} from 'reactstrap';
import { useMutation, useQuery } from '@apollo/client';
import styled from 'styled-components';
import XIcon from './icons/XIcon';
import { UpdateFarmMutation } from '../mutations';
import { AllVisibleTagsQuery } from '../queries';
import { captureEvent } from '../utils/analytics';
import { farmCanBeEdited } from '../utils/utils';

// padding should be set on the child element, not here
// so that the highlight on hover effect for the x icon looks correct
const TagPillDiv = styled.div`
  border-radius: 0.5rem;
  background: ${({ background }) => background || '#eeeeee'};
  color: var(--text-color);
  margin: 2px;
  width: fit-content;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
  &:hover {
    ${({ activeOnHover }) => (activeOnHover ? 'background: #e8e8e8' : '')};
  }
`;

const TagPillInput = styled.input`
  width: 100%;
  border-radius: 0.5rem;
  border: 1px solid #ced4da;
  padding: 2px 4px;
`;

const HighlightedXIcon = styled.div`
  display: flex;
  border-radius: 0.5rem;
  height: 100%;
  width: fit-content;
  padding: 2px 6px;
  align-items: center;
  &:hover {
    ${({ mutating }) => (!mutating ? 'background: #ced4da' : '')};
  }
`;

const TagTextWithPadding = styled.div`
  padding: 2px 4px 2px 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TagPill = ({
  tag,
  onAdd,
  onChange,
  onDelete,
  canEdit,
  mutating,
  addingNewTag,
  setAddingNewTag,
  background,
}) => {
  const [editing, setEditing] = useState(addingNewTag);
  if (editing) {
    return (
      <TagPillInput
        defaultValue={tag}
        autoFocus
        onKeyUp={(e) => {
          if (e.code === 'Enter') {
            e.target.blur();
          }
        }}
        onBlur={async (e) => {
          const trimmedValue = e.target.value.trim();
          try {
            // if there was no existing tag before...
            if (!tag) {
              if (!trimmedValue) {
                // this was a new input, with no initial value, and we've blurred without adding a value
                setAddingNewTag(false);
              } else {
                // this was a new input, with no initial value, and we've blurred with a value, so add it
                await onAdd(trimmedValue);
                setAddingNewTag(false);
              }
            } else if (!trimmedValue) {
              // this was an existing value, and we've blurred with it deleted, so delete it
              await onDelete(tag);
            } else if (tag !== trimmedValue) {
              // this was an existing value, and we've blurred with a new value, so change it
              await onChange(tag, trimmedValue);
            }
          } finally {
            setEditing(false);
          }
        }}
      />
    );
  }
  return (
    <TagPillDiv
      title={tag}
      className="d-flex align-items-center"
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (!mutating && canEdit) setEditing(true);
      }}
      activeOnHover={canEdit && !mutating}
      background={background}
    >
      <TagTextWithPadding>{tag}</TagTextWithPadding>
      {canEdit && (
        <HighlightedXIcon
          onClick={async () => {
            await onDelete(tag);
          }}
          mutating={mutating}
        >
          <XIcon
            className="x-icon flex-shrink-0"
            fill={mutating ? 'rgba(33, 33, 33, 0.3)' : 'rgba(33, 33, 33, 0.5)'}
          />
        </HighlightedXIcon>
      )}
    </TagPillDiv>
  );
};

const PillContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  min-height: 18px;
  overflow: hidden;
  .add-tag-button {
    visibility: hidden;
  }
  &:hover {
    .add-tag-button {
      visibility: visible;
    }
  }
`;

const StyledMenu = styled(DropdownMenu)`
  max-height: 300px;
  overflow: hidden;
  overflow-y: scroll;
`;

const FarmTagsCell = ({ tags: farmTags, item, mutateAndSetState }) => {
  const canEdit = item?.allowedActions?.canEdit && farmCanBeEdited(item);
  const [updateMutation] = useMutation(UpdateFarmMutation, {
    refetchQueries: ['AllVisibleTags'],
  });
  const [addingNewTag, setAddingNewTag] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mutating, setMutating] = useState(false);

  const { data: tagsData } = useQuery(AllVisibleTagsQuery);

  const updateFarmTags = async (tags) => {
    // Because tags is a string array, it doesn't handle updates to multiple tags at once well
    // so, block more updates until the current one is done
    if (mutating) return;
    try {
      setMutating(true);
      await mutateAndSetState(() =>
        updateMutation({
          variables: {
            input: {
              id: item.id,
              updatedAt: item.updatedAt,
              tags,
            },
          },
        }),
      );
    } finally {
      setMutating(false);
    }
  };

  const onDelete = async (tagToDelete) => {
    if (!canEdit) return;
    captureEvent('farm-tag-deleted', { location: 'project table' });
    await updateFarmTags(farmTags.filter((tag) => tag !== tagToDelete));
  };

  const onAdd = async (tagToAdd) => {
    if (!canEdit) return;
    captureEvent('farm-tag-added', { location: 'project table' });
    await updateFarmTags([...farmTags, tagToAdd]);
  };

  const onChange = async (oldTag, newTag) => {
    if (!canEdit) return;
    captureEvent('farm-tag-changed', { location: 'project table' });
    await updateFarmTags(
      farmTags.map((tag) => (tag === oldTag ? newTag : tag)),
    );
  };

  return (
    <PillContainer>
      {item.isLocked && <TagPill tag="Locked" background="#d6d6d6" />}
      {item.isArchived && <TagPill tag="Archived" background="#d6d6d6" />}
      {farmTags?.map((tag) => (
        <TagPill
          key={tag}
          tag={tag}
          canEdit={canEdit}
          onAdd={onAdd}
          onChange={onChange}
          onDelete={onDelete}
          mutating={mutating}
        />
      ))}
      {addingNewTag && (
        <TagPill
          tag=""
          canEdit={canEdit}
          onAdd={onAdd}
          onChange={onChange}
          onDelete={onDelete}
          addingNewTag={addingNewTag}
          setAddingNewTag={setAddingNewTag}
          mutating={mutating}
        />
      )}
      {canEdit && !mutating && !addingNewTag && (
        <Dropdown
          isOpen={dropdownOpen}
          toggle={() => {
            setDropdownOpen(!dropdownOpen);
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownToggle color="link" className="px-1 py-0 add-tag-button">
            +
          </DropdownToggle>
          <StyledMenu positionFixed>
            {tagsData?.allVisibleTags
              ?.filter((tagOption) => !farmTags.includes(tagOption))
              ?.map((tagOption) => (
                <DropdownItem
                  key={tagOption}
                  onClick={async () => {
                    await onAdd(tagOption);
                  }}
                >
                  {tagOption}
                </DropdownItem>
              ))}
            {tagsData?.allVisibleTags?.length !== 0 && <DropdownItem divider />}
            <DropdownItem onClick={() => setAddingNewTag(true)}>
              + New Tag
            </DropdownItem>
          </StyledMenu>
        </Dropdown>
      )}
      {mutating && <Spinner size="sm" className="align-self-center" />}
    </PillContainer>
  );
};

export default FarmTagsCell;
